/**
 * Task List Component
 *
 * Displays a list of tasks with filtering and grouping options.
 */

import { useState } from 'react';
import type { Task, TaskGroupBy } from '@/types';
import { TaskCard } from './task-card';

interface TaskListProps {
  tasks: Task[];
  isLoading?: boolean;
  onComplete?: (taskId: string) => void;
  onReopen?: (taskId: string) => void;
  onDelete?: (taskId: string) => void;
  onEdit?: (task: Task) => void;
  onTaskClick?: (task: Task) => void;
  onFocusToggle?: (taskId: string) => void;
  groupBy?: TaskGroupBy;
  emptyMessage?: string;
}

type GroupedTasks = Record<string, Task[]>;

function groupTasks(tasks: Task[], groupBy: TaskGroupBy): GroupedTasks {
  const groups: GroupedTasks = {};

  for (const task of tasks) {
    let groupKey: string;

    switch (groupBy) {
      case 'status':
        groupKey = task.status;
        break;
      case 'priority':
        groupKey = task.priority;
        break;
      case 'dueDate':
        if (!task.dueAt) {
          groupKey = 'No Due Date';
        } else {
          const dueDate = new Date(task.dueAt);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const daysDiff = Math.floor(
            (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
          );

          if (daysDiff < 0) {
            groupKey = 'Overdue';
          } else if (daysDiff === 0) {
            groupKey = 'Today';
          } else if (daysDiff <= 7) {
            groupKey = 'This Week';
          } else {
            groupKey = 'Later';
          }
        }
        break;
      case 'topic':
        groupKey = task.topicPath || 'No Topic';
        break;
      case 'assignee':
        groupKey = task.assigneeId || 'Unassigned';
        break;
      default:
        groupKey = 'All';
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(task);
  }

  return groups;
}

const groupOrder: Record<TaskGroupBy, string[]> = {
  status: ['pending', 'in_progress', 'completed', 'cancelled'],
  priority: ['high', 'medium', 'low'],
  dueDate: ['Overdue', 'Today', 'This Week', 'Later', 'No Due Date'],
  topic: [], // Dynamic
  assignee: [], // Dynamic
};

const groupLabels: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  high: 'High Priority',
  medium: 'Medium Priority',
  low: 'Low Priority',
};

export function TaskList({
  tasks,
  isLoading,
  onComplete,
  onReopen,
  onDelete,
  onEdit,
  onTaskClick,
  onFocusToggle,
  groupBy,
  emptyMessage = 'No tasks found',
}: TaskListProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="task-list-loading">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-20 bg-gray-100 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500" data-testid="task-list-empty">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
        <p className="mt-2">{emptyMessage}</p>
      </div>
    );
  }

  // Render grouped tasks
  if (groupBy) {
    const groups = groupTasks(tasks, groupBy);
    const order = groupOrder[groupBy].length > 0
      ? groupOrder[groupBy]
      : Object.keys(groups).sort();

    return (
      <div className="space-y-6">
        {order
          .filter((key) => groups[key]?.length > 0)
          .map((groupKey) => (
            <div key={groupKey}>
              {/* Group header */}
              <button
                onClick={() => toggleGroup(groupKey)}
                className="flex items-center gap-2 w-full text-left mb-2 group"
              >
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    collapsedGroups.has(groupKey) ? '' : 'rotate-90'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-gray-700">
                  {groupLabels[groupKey] || groupKey}
                </h3>
                <span className="text-sm text-gray-400">
                  ({groups[groupKey].length})
                </span>
              </button>

              {/* Tasks in group */}
              {!collapsedGroups.has(groupKey) && (
                <div className="space-y-2 pl-6">
                  {groups[groupKey].map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onComplete={onComplete}
                      onReopen={onReopen}
                      onDelete={onDelete}
                      onEdit={onEdit}
                      onClick={onTaskClick}
                      onFocusToggle={onFocusToggle}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
    );
  }

  // Render flat list
  return (
    <div className="space-y-2" data-testid="task-list">
      {tasks.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          onComplete={onComplete}
          onReopen={onReopen}
          onDelete={onDelete}
          onEdit={onEdit}
          onClick={onTaskClick}
          onFocusToggle={onFocusToggle}
        />
      ))}
    </div>
  );
}
